/* Importing the necessary libraries and files */
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import * as Yup from 'yup';
import { Form, FormikProvider, useFormik } from 'formik';

/* Importing the components from the material-ui library. */
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { Close as CloseIcon, DevicesOther as DevicesOtherIcon, VideoCall as VideoCallIcon } from '@mui/icons-material';

/* Importing components. */
import AutoCompleteDevice from '../../components/AutoCompleteDevice';

/* Importing function from the utils folder. */
import { fDevice } from '../../utils/Device/Device.utils.format';

const IconWrapperStyle = styled('div')(({ theme }) => ({
  margin: 'auto',
  display: 'flex',
  borderRadius: '50%',
  alignItems: 'center',
  width: theme.spacing(8),
  height: theme.spacing(8),
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
}));

/**
 * Renders a component for selecting a device.
 *
 * @returns {JSX.Element} The rendered component.
 */
export default function SelectDevice() {
  const colorDevice = 'primary';

  const navigate = useNavigate();

  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const { Devices } = useSelector((state) => state.device);

  /* Creating a schema for the Device form. */
  const DeviceSchema = Yup.object().shape({
    subject: Yup.string().required('Device required'),
  });

  /* Using the useFormik hook to create a formik object. */
  const formik = useFormik({
    initialValues: {
      subject: null,
    },
    validationSchema: DeviceSchema,
    onSubmit: (values) => {
      const currentDevice = Devices.find((device) => device.id === values.subject);
      const deviceObj = fDevice(currentDevice);
      navigate(`/meeting/call/${deviceObj.deviceName_name}`, { replace: false, state: { new: true } });
    },
  });

  const { errors, touched, handleSubmit, resetForm, setFieldValue, values } = formik;

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  return (
    <>
      <Card>
        <CardActionArea
          sx={{
            py: 5,
            boxShadow: 3,
            textAlign: 'center',
          }}
          onClick={handleClickOpen}
        >
          <IconWrapperStyle
            sx={{
              color: (theme) => theme.palette[colorDevice].dark,
              backgroundImage: (theme) =>
                `linear-gradient(135deg, ${alpha(theme.palette[colorDevice].dark, 0)} 0%, ${alpha(
                  theme.palette[colorDevice].dark,
                  0.24
                )} 100%)`,
            }}
          >
            <DevicesOtherIcon />
          </IconWrapperStyle>
          <Typography variant="h6">Call a Device</Typography>
        </CardActionArea>
      </Card>
      <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogTitle>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="h6">Select a Device</Typography>
                <IconButton onClick={handleClose} aria-label="Close">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
                <AutoCompleteDevice
                  setFieldValue={setFieldValue}
                  fieldName="subject"
                  values={values}
                  touched={touched}
                  errors={errors}
                  label="Device"
                />
                {values.subject && (
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button variant="contained" type="submit" startIcon={<VideoCallIcon />} sx={{ width: '250px' }}>
                      Call device
                    </Button>
                  </Box>
                )}
              </Stack>
            </DialogContent>
          </Form>
        </FormikProvider>
      </Dialog>
    </>
  );
}
